// Simple event emitter for browser environment
import { authService } from '../lib/auth';

type EventHandler = (data: any) => void;
type EventMap = Map<string, Set<EventHandler>>;

export interface WebSocketMessage {
  type: 'inbox_update' | 'new_item' | 'item_updated' | 'item_deleted' | 'email_opened' | 'notification';
  payload: any;
  timestamp: string;
}

export interface InboxUpdatePayload {
  item_id: number;
  changes?: Partial<any>;
  action: 'created' | 'updated' | 'deleted' | 'read' | 'assigned' | 'snoozed' | 'archived';
}

export interface EmailOpenedPayload {
  message_id: number;
  thread_id: number;
  opened_at: string;
  recipient: string;
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  // Bumped by disconnect() so an in-flight token refresh doesn't reconnect.
  private connectGeneration = 0;
  private messageQueue: any[] = []; // Queue raw data objects, not WebSocketMessage
  private pingInterval: number | null = null;
  private events: EventMap = new Map();
  private authErrorDetected = false; // Track auth failures to prevent infinite retries

  constructor() {
    // Initialize event system
  }

  // Event emitter methods
  on(event: string, handler: EventHandler): void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)?.add(handler);
  }

  off(event: string, handler: EventHandler): void {
    this.events.get(event)?.delete(handler);
  }

  emit(event: string, data?: any): void {
    this.events.get(event)?.forEach(handler => handler(data));
  }

  connect(organizationId?: string, skipTokenRefresh = false): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    // Don't reconnect if auth error was detected
    if (this.authErrorDetected) {
      console.warn('WebSocket: Auth error detected, not attempting reconnect. Please re-authenticate.');
      this.emit('auth_error');
      return;
    }

    // The token is only checked at the handshake, so a reconnect after the
    // access token expires would be rejected on every retry. Refresh first.
    if (!skipTokenRefresh && authService.isAccessTokenExpiring()) {
      this.isConnecting = true;
      const generation = this.connectGeneration;
      void authService.ensureFreshAccessToken().finally(() => {
        if (generation !== this.connectGeneration) {
          return; // disconnect() was called while refreshing
        }
        this.isConnecting = false;
        this.connect(organizationId, true);
      });
      return;
    }

    this.isConnecting = true;
    
    // Get JWT token for authentication
    let token: string | null = null;
    try {
      token = authService.getAccessToken();
    } catch (error) {
      console.warn('WebSocket: Could not get access token:', error);
    }
    
    // Use same origin WebSocket connection
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const path = organizationId ? `/ws/inbox/${organizationId}/` : '/ws/inbox/';
    const url = `${protocol}//${host}${path}`;
    const subprotocols = token ? ['access_token', token] : undefined;

    try {
      this.ws = subprotocols ? new WebSocket(url, subprotocols) : new WebSocket(url);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.authErrorDetected = false; // Reset auth error flag on successful connection
        this.emit('connected');
        
        // Send any queued messages
        this.flushMessageQueue();
        
        // Start ping interval to keep connection alive
        this.startPingInterval();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
        this.emit('error', error);
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket disconnected', event.code, event.reason);
        this.isConnecting = false;
        this.ws = null;
        this.stopPingInterval();
        this.emit('disconnected');
        
        // Check for auth errors (401 Unauthorized, 403 Forbidden)
        // WebSocket close codes: 1008 = policy violation, 1002 = protocol error
        // We'll also check the reason string for auth-related messages
        const isAuthError = event.code === 1008 || 
                          event.code === 1002 ||
                          (event.reason && (
                            event.reason.toLowerCase().includes('unauthorized') ||
                            event.reason.toLowerCase().includes('forbidden') ||
                            event.reason.toLowerCase().includes('authentication') ||
                            event.reason.toLowerCase().includes('permission')
                          ));
        
        if (isAuthError) {
          console.error('WebSocket: Authentication error detected, stopping reconnection attempts');
          this.authErrorDetected = true;
          this.emit('auth_error', { code: event.code, reason: event.reason });
          return;
        }
        
        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && event.code !== 1001) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  private handleMessage(message: WebSocketMessage): void {
    // Emit specific events based on message type
    this.emit(message.type, message.payload);
    
    // Also emit a general message event
    this.emit('message', message);
    
    // Handle specific message types
    switch (message.type) {
      case 'inbox_update':
        this.handleInboxUpdate(message.payload as InboxUpdatePayload);
        break;
      case 'email_opened':
        this.handleEmailOpened(message.payload as EmailOpenedPayload);
        break;
      case 'new_item':
        this.emit('new_inbox_item', message.payload);
        break;
      case 'notification':
        this.emit('notification', message.payload);
        break;
    }
  }

  private handleInboxUpdate(payload: InboxUpdatePayload): void {
    // Emit specific action events
    this.emit(`item_${payload.action}`, payload);
  }

  private handleEmailOpened(payload: EmailOpenedPayload): void {
    // Emit email opened event with details
    this.emit('email_opened_notification', payload);
  }

  private scheduleReconnect(): void {
    // Don't reconnect if auth error detected or max attempts reached
    if (this.reconnectTimer || this.reconnectAttempts >= this.maxReconnectAttempts || this.authErrorDetected) {
      if (this.authErrorDetected) {
        console.warn('WebSocket: Skipping reconnect due to auth error');
      }
      return;
    }

    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      30000
    );

    console.log(`Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  private startPingInterval(): void {
    this.stopPingInterval();
    
    // Send ping every 30 seconds to keep connection alive
    this.pingInterval = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping' });
      }
    }, 30000);
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      window.clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private flushMessageQueue(): void {
    while (this.messageQueue.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      const message = this.messageQueue.shift();
      if (message) {
        this.ws.send(JSON.stringify(message));
      }
    }
  }

  send(data: any): void {
    const message = {
      ...data,
      timestamp: new Date().toISOString(),
    };

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      // Queue message to send when connected
      this.messageQueue.push(message);
      
      // Try to connect if not already
      if (!this.isConnecting) {
        this.connect();
      }
    }
  }

  disconnect(): void {
    this.connectGeneration++;
    this.stopPingInterval();
    
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    
    this.messageQueue = [];
    this.reconnectAttempts = 0;
    this.isConnecting = false;
    this.authErrorDetected = false; // Reset on manual disconnect
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getReadyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }
}

// Export singleton instance
const websocketService = new WebSocketService();
export default websocketService;